/**
 * chatStore.js — Pinia store per il sistema Chat (schema chat2)
 *
 * Architettura offline-first:
 * - Le room e i messaggi vengono salvati in Dexie (chat2_room, chat2_message, chat2_message_read)
 * - L'invio di messaggi è ottimistico: appare subito in UI, poi si sincronizza
 * - La ricezione avviene tramite FCM (push notification) che triggera un pull
 * - Il canale SSE esistente trasmette anche aggiornamenti chat via sync_update
 *
 * Utilizzo:
 *   const chatStore = useChatStore()
 *   await chatStore.loadRooms()
 *   await chatStore.openRoom(id_room)
 *   await chatStore.sendMessage(id_room, body)
 */

import { defineStore } from 'pinia'
import { api } from '../services/api.js'
import { useDbStore } from './dbStore.js'
import { useAppStore } from './app.js'

// Lunghezza massima del poll (secondi tra due pull dei messaggi quando la room è aperta)
const POLL_INTERVAL_MS = 30_000

function generateUUID() {
  return crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16)
      })
}

export const useChatStore = defineStore('chat', {
  state: () => ({
    // Lista room con ultimo messaggio e unread count
    rooms: [],
    roomsLoading: false,
    roomsError: null,

    // Messaggi della room corrente
    currentRoomId: null,
    messages: [],          // ordinati dal più vecchio al più recente (inverso del server)
    messagesLoading: false,
    hasMoreMessages: true, // false quando il server non ne ritorna più
    messagesOffset: 0,
    MESSAGES_LIMIT: 50,

    // Contatti disponibili per nuove chat
    contacts: [],
    contactsLoading: false,

    // Stato invio
    sending: false,
    sendError: null,

    // Poll timer per la room aperta
    _pollTimer: null,
  }),

  getters: {
    totalUnread: (state) =>
      state.rooms.reduce((sum, r) => sum + (r.unread_count || 0), 0),

    currentRoom: (state) =>
      state.rooms.find(r => r.id_room === state.currentRoomId) || null,
  },

  actions: {
    // ── Rooms ────────────────────────────────────────────────────────────────

    async loadRooms() {
      this.roomsLoading = true
      this.roomsError   = null
      try {
        const res = await api.get('/api/v2/chat/rooms')
        if (!res.ok) throw new Error(res.message || 'Errore caricamento chat')
        this.rooms = res.data || []
        // Sincronizza in Dexie
        await this._saveRoomsToDexie(this.rooms)
      } catch (e) {
        this.roomsError = e.message
        // Fallback: leggi da Dexie
        await this._loadRoomsFromDexie()
      } finally {
        this.roomsLoading = false
      }
    },

    async _saveRoomsToDexie(rooms) {
      const dbStore = useDbStore()
      if (!dbStore.isReady) return
      // Le room virtuali (id_room=null) non vanno persiste: null non è una chiave IDB valida
      const real = rooms.filter(r => r.id_room != null)
      if (!real.length) return
      await dbStore.db.table('chat2_room').bulkPut(real.map(r => ({ ...r })))
    },

    async _loadRoomsFromDexie() {
      const dbStore = useDbStore()
      if (!dbStore.isReady) return
      const rows = await dbStore.db.table('chat2_room').toArray()
      this.rooms = rows
    },

    // ── Messaggi ─────────────────────────────────────────────────────────────

    async openRoom(id_room) {
      if (this.currentRoomId === id_room) return
      this._stopPoll()
      this.currentRoomId   = id_room
      this.messages        = []
      this.messagesOffset  = 0
      this.hasMoreMessages = true
      await this.loadMessages()
      await this.markRead(id_room)
      this._startPoll(id_room)
    },

    closeRoom() {
      this._stopPoll()
      this.currentRoomId = null
      this.messages      = []
    },

    async loadMessages(prepend = false) {
      if (!this.currentRoomId) return
      if (!this.hasMoreMessages && prepend) return

      this.messagesLoading = true
      const offset = prepend ? this.messagesOffset : 0

      try {
        const res = await api.get(
          `/api/v2/chat/rooms/${this.currentRoomId}/messages`,
          { params: { limit: this.MESSAGES_LIMIT, offset } }
        )
        if (!res.ok) throw new Error(res.message || 'Errore messaggi')

        // Il server ritorna dal più recente al più vecchio — invertiamo per la UI
        const fetched = (res.data || []).reverse()

        if (prepend) {
          this.messages       = [...fetched, ...this.messages]
          this.messagesOffset += fetched.length
        } else {
          this.messages       = fetched
          this.messagesOffset = fetched.length
        }

        this.hasMoreMessages = fetched.length >= this.MESSAGES_LIMIT
        await this._saveMessagesToDexie(fetched)
      } catch (e) {
        if (!prepend) {
          // Solo al primo caricamento prova Dexie come fallback
          await this._loadMessagesFromDexie()
        }
      } finally {
        this.messagesLoading = false
      }
    },

    async _saveMessagesToDexie(messages) {
      const dbStore = useDbStore()
      if (!dbStore.isReady) return
      await dbStore.db.table('chat2_message').bulkPut(messages.map(m => ({ ...m })))
    },

    async _loadMessagesFromDexie() {
      const dbStore = useDbStore()
      if (!dbStore.isReady) return
      const rows = await dbStore.db
        .table('chat2_message')
        .where('id_room').equals(this.currentRoomId)
        .sortBy('created_at')
      this.messages = rows
    },

    // ── Invio messaggio ───────────────────────────────────────────────────────

    async sendMessage(id_room, body) {
      if (!body?.trim()) return
      const clientId = generateUUID()
      const appStore = useAppStore()
      const senderUid  = String(appStore.user?.uid || '')
      const senderName = appStore.getUserName || appStore.user?.email || ''

      // Messaggio ottimistico
      const optimistic = {
        id_message:  clientId,
        id_room,
        client_id:   clientId,
        sender_uid:  senderUid,
        sender_name: senderName,
        body:        body.trim(),
        created_at:  new Date().toISOString(),
        my_read_at:  null,
        _optimistic: true,
        _to_synch:   true,
      }

      if (this.currentRoomId === id_room) {
        this.messages = [...this.messages, optimistic]
      }

      // Salva in Dexie con _to_synch
      const dbStore = useDbStore()
      if (dbStore.isReady) {
        await dbStore.db.table('chat2_message').put({ ...optimistic })
      }

      try {
        const res = await api.post(
          `/api/v2/chat/rooms/${id_room}/messages`,
          { body: body.trim(), client_id: clientId }
        )
        if (!res.ok) throw new Error(res.message || 'Errore invio')

        // Aggiorna messaggio ottimistico con id_message dal server
        const confirmed = {
          ...optimistic,
          ...res.data,
          _optimistic: false,
          _to_synch:   false,
        }
        if (this.currentRoomId === id_room) {
          this.messages = this.messages.map(m =>
            m.client_id === clientId ? confirmed : m
          )
        }
        if (dbStore.isReady) {
          await dbStore.db.table('chat2_message').put({ ...confirmed })
          // Rimuovi la versione ottimistica (chiave diversa)
          if (confirmed.id_message !== clientId) {
            await dbStore.db.table('chat2_message').delete(clientId)
          }
        }

        // Aggiorna last_body nella lista room
        this._updateRoomLastMessage(id_room, body.trim())

      } catch (e) {
        // Messaggio resta in Dexie con _to_synch: true — sarà inviato al prossimo sync
        console.warn('[chat] send failed, queued for sync:', e.message)
        // Rimuovi flag ottimistico per mostrare icona "in attesa"
        if (this.currentRoomId === id_room) {
          this.messages = this.messages.map(m =>
            m.client_id === clientId ? { ...m, _send_error: true } : m
          )
        }
      }
    },

    // ── Lettura ───────────────────────────────────────────────────────────────

    async markRead(id_room) {
      try {
        await api.post(`/api/v2/chat/rooms/${id_room}/read`, {})
        // Azzera unread count localmente
        const room = this.rooms.find(r => r.id_room === id_room)
        if (room) room.unread_count = 0
      } catch (e) {
        // Non critico
      }
    },

    // ── Contatti ─────────────────────────────────────────────────────────────

    async loadContacts() {
      this.contactsLoading = true
      try {
        const res = await api.get('/api/v2/chat/contacts')
        if (!res.ok) throw new Error(res.message || 'Errore contatti')
        this.contacts = res.data || []
      } catch (e) {
        console.warn('[chat] contacts error:', e.message)
      } finally {
        this.contactsLoading = false
      }
    },

    // ── Creazione room ────────────────────────────────────────────────────────

    async getOrCreateDirect(otherUid) {
      const res = await api.post('/api/v2/chat/rooms/direct', { uid: otherUid })
      if (!res.ok) throw new Error(res.message || 'Errore creazione chat')
      await this.loadRooms()
      return res.data.id_room
    },

    async getOrCreateFarm(idFarmCenter) {
      const res = await api.post(`/api/v2/chat/rooms/farm/${idFarmCenter}`)
      if (!res.ok) throw new Error(res.message || 'Errore creazione chat')
      await this.loadRooms()
      return res.data.id_room
    },

    // ── Sync in arrivo (chiamato da dbStore quando SSE porta aggiornamenti chat) ──

    async onIncomingMessages(newMessages) {
      if (!newMessages?.length) return
      const dbStore = useDbStore()
      if (dbStore.isReady) {
        await dbStore.db.table('chat2_message').bulkPut(newMessages.map(m => ({ ...m })))
      }
      // Se la room è aperta, aggiorna la lista
      const forCurrentRoom = newMessages.filter(m => m.id_room === this.currentRoomId)
      if (forCurrentRoom.length > 0) {
        for (const msg of forCurrentRoom) {
          const exists = this.messages.find(m => m.id_message === msg.id_message)
          if (!exists) this.messages = [...this.messages, msg]
        }
        await this.markRead(this.currentRoomId)
      }
      // Aggiorna unread count nelle room
      await this.loadRooms()
    },

    // ── Helpers privati ───────────────────────────────────────────────────────

    _updateRoomLastMessage(id_room, body) {
      const room = this.rooms.find(r => r.id_room === id_room)
      if (room) {
        room.last_body       = body
        room.last_message_at = new Date().toISOString()
      }
    },

    _startPoll(id_room) {
      this._stopPoll()
      this._pollTimer = setInterval(async () => {
        if (this.currentRoomId !== id_room) {
          this._stopPoll()
          return
        }
        // Pull silenzioso: aggiunge solo messaggi più nuovi
        try {
          const res = await api.get(
            `/api/v2/chat/rooms/${id_room}/messages`,
            { params: { limit: 20, offset: 0 } }
          )
          if (!res.ok) return
          const fetched = (res.data || []).reverse()
          const lastKnown = this.messages[this.messages.length - 1]?.created_at
          const newOnes = lastKnown
            ? fetched.filter(m => m.created_at > lastKnown)
            : fetched
          if (newOnes.length > 0) {
            this.messages = [...this.messages, ...newOnes]
            await this.markRead(id_room)
            await this._saveMessagesToDexie(newOnes)
          }
        } catch { /* silenzioso */ }
      }, POLL_INTERVAL_MS)
    },

    _stopPoll() {
      if (this._pollTimer) {
        clearInterval(this._pollTimer)
        this._pollTimer = null
      }
    },
  },
})
